import React, { Fragment, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, FolderPlusIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { toast } from "sonner";
import {
  createDocumentFolder,
  DOCUMENT_ENTITY_TYPES,
} from "services/documentFolderService";

/**
 * Modal to create a new subfolder
 */
export function CreateFolderModal({
  isOpen,
  onClose,
  onCreateSuccess,
  entityId,
  parentFolderId,
  rootFolderId,
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
}) {
  const [folderName, setFolderName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFolderName("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!folderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }

    const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
    const sanitizeId = (id) => (id && id !== EMPTY_GUID ? id : null);
    const targetParentId = sanitizeId(parentFolderId) || sanitizeId(rootFolderId);

    setIsSubmitting(true);
    try {
      const res = await createDocumentFolder({
        entityType,
        entityId,
        parentFolderId: targetParentId,
        name: folderName,
      });

      if (res.success) {
        toast.success(res.message || "Folder created successfully");
        onClose();
        if (onCreateSuccess) onCreateSuccess(res.data);
      } else {
        toast.error(res.error || "Failed to create folder");
      }
    } catch (err) {
      toast.error(err.message || "Error creating folder");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[300] flex flex-col items-center justify-center p-4 sm:px-5"
        onClose={() => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-md origin-top flex-col overflow-hidden rounded-lg bg-white shadow-xl transition-all">
            <div className="dark:bg-dark-800 flex items-center justify-between bg-gray-100 px-4 py-3 sm:px-5 border-b border-gray-200 dark:border-dark-600">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-semibold text-gray-900"
              >
                <FolderPlusIcon className="h-5 w-5 text-amber-500" />
                New Folder
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full"
                disabled={isSubmitting}
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
              <div>
                <label
                  htmlFor="create-folder-name"
                  className="block text-xs font-medium text-gray-700 dark:text-dark-200 mb-1"
                >
                  Folder Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-folder-name"
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="e.g. Identity Documents"
                  maxLength={50}
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-50 dark:placeholder-dark-400"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[5.5rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  className="min-w-[5.5rem] rounded-full"
                  disabled={isSubmitting || !folderName.trim()}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </Button>
              </div>
            </form>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

export default CreateFolderModal;
